/**
 * Screenshot / screen-recording deterrents for web.
 *
 * NOTE — context for future maintainers:
 *   Web browsers do NOT expose any API to truly block screenshots or screen
 *   recordings. The native equivalent is Android's `FLAG_SECURE` / iOS's
 *   `isCaptured`, neither of which is reachable from a normal web app.
 *
 *   This module installs LAYERED DETERRENTS that defeat casual capture:
 *     1. Disables right-click + drag + text selection (no easy "Save image…").
 *     2. Blocks the PrintScreen / Snipping Tool keyboard shortcuts that the
 *        browser CAN intercept (PrintScreen on Windows, Meta+Shift+S, etc.)
 *        and copies an empty string to the clipboard.
 *     3. Blanks the page whenever the tab loses focus or the document is
 *        hidden — defeats most screen recorders that wait for app-switch.
 *     4. Adds a `@media print` rule that hides every element except a
 *        warning notice → printing yields a useless page.
 *     5. Adds a semi-visible session watermark (email · timestamp) so any
 *        leaked screenshot is attributable.
 *
 * Toggle off via `localStorage.flexpay_screenshot_deterrent = "off"` for
 * support sessions (never expose this in the UI).
 */
const DETERRENT_STYLE_ID = "flexpay-screenshot-deterrent-style";
const BLANK_OVERLAY_ID = "flexpay-screenshot-blank-overlay";

let attached = false;
let watermarkInterval = null;

function injectStyles() {
  if (document.getElementById(DETERRENT_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = DETERRENT_STYLE_ID;
  style.textContent = `
    /* Layer 1: deter casual save / select / drag. */
    html, body {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
    }
    /* Keep inputs/textareas usable — selection is essential there. */
    input, textarea, [contenteditable="true"], [data-allow-select="true"] {
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
      user-select: text !important;
    }
    img, svg { -webkit-user-drag: none; user-drag: none; pointer-events: auto; }

    /* Layer 3: blank everything when tab/window loses focus. */
    body.flexpay-blanked > *:not(#${BLANK_OVERLAY_ID}) {
      visibility: hidden !important;
    }
    #${BLANK_OVERLAY_ID} {
      position: fixed; inset: 0; z-index: 999999;
      background: #000; color: #fff;
      display: none; align-items: center; justify-content: center;
      font: 600 18px/1.4 system-ui, sans-serif; text-align: center; padding: 24px;
    }
    body.flexpay-blanked #${BLANK_OVERLAY_ID} { display: flex; }

    /* Layer 4: kill printing entirely. */
    @media print {
      html, body { background: #000 !important; }
      body * { visibility: hidden !important; }
      body::after {
        content: "Printing this page is disabled for security. — FlexPay";
        position: fixed; inset: 0;
        display: flex; align-items: center; justify-content: center;
        visibility: visible !important;
        font: 700 22px/1.4 system-ui, sans-serif; color: #fff;
      }
    }

    /* Layer 5: persistent diagonal watermark with the signed-in identifier. */
    .flexpay-watermark {
      position: fixed; inset: 0; pointer-events: none;
      z-index: 1; opacity: 0.06;
      background-repeat: repeat;
      background-image: var(--flexpay-watermark-image, none);
    }
  `;
  document.head.appendChild(style);

  const overlay = document.createElement("div");
  overlay.id = BLANK_OVERLAY_ID;
  overlay.setAttribute("role", "presentation");
  overlay.textContent = "Content hidden — please return to the FlexPay tab to resume.";
  document.body.appendChild(overlay);
}

function setBlanked(on) {
  if (!document.body) return;
  document.body.classList.toggle("flexpay-blanked", !!on);
}

const onVisibility = () => setBlanked(document.visibilityState !== "visible");
const onBlur = () => setBlanked(true);
const onFocus = () => setBlanked(false);

const onContextMenu = (e) => { e.preventDefault(); };
const onDragStart = (e) => { e.preventDefault(); };

const onKeyDown = (e) => {
  const k = (e.key || "").toLowerCase();
  // PrintScreen — browsers CAN cancel it in some envs; also overwrite the clipboard.
  if (k === "printscreen") {
    try { navigator.clipboard?.writeText?.(""); } catch { /* ignore */ }
    setBlanked(true);
    setTimeout(() => setBlanked(false), 1200);
    e.preventDefault();
    return;
  }
  // Block common save/print/source-view shortcuts. (Devtools cannot be blocked.)
  const meta = e.ctrlKey || e.metaKey;
  if (meta && ["s", "p"].includes(k)) { e.preventDefault(); }
  // Win+Shift+S (Snipping Tool launcher) — best-effort blank.
  if (e.shiftKey && (e.metaKey || e.key === "Meta") && k === "s") {
    setBlanked(true);
    setTimeout(() => setBlanked(false), 1500);
    e.preventDefault();
  }
};

const onCopy = (e) => {
  // Allow copy from inputs / textareas / explicit allow-select regions only.
  const t = e.target;
  if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable || t.closest?.('[data-allow-select="true"]'))) {
    return;
  }
  e.preventDefault();
};

function applyWatermark(label) {
  if (!label) {
    document.documentElement.style.removeProperty("--flexpay-watermark-image");
    document.querySelector(".flexpay-watermark")?.remove();
    return;
  }
  // Build a tiny inline SVG with rotated repeating text.
  const stamp = `${label} · ${new Date().toLocaleString()}`;
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='420' height='180'>
       <text x='0' y='100' transform='rotate(-22 0 100)' fill='currentColor'
             font-family='system-ui,sans-serif' font-size='14' font-weight='600'>${stamp}</text>
     </svg>`
  );
  document.documentElement.style.setProperty("--flexpay-watermark-image", `url("data:image/svg+xml,${svg}")`);
  if (!document.querySelector(".flexpay-watermark")) {
    const wm = document.createElement("div");
    wm.className = "flexpay-watermark";
    document.body.appendChild(wm);
  }
}

export function setWatermarkIdentity(label) {
  applyWatermark(label || "");
  // Refresh once a minute so the stamped timestamp stays current.
  if (watermarkInterval) clearInterval(watermarkInterval);
  if (label) {
    watermarkInterval = setInterval(() => applyWatermark(label), 60_000);
  }
}

export function installScreenshotDeterrent() {
  if (attached) return;
  if (typeof window === "undefined") return;
  if (window.localStorage?.getItem("flexpay_screenshot_deterrent") === "off") return;

  injectStyles();
  document.addEventListener("contextmenu", onContextMenu);
  document.addEventListener("dragstart", onDragStart);
  document.addEventListener("keydown", onKeyDown, { capture: true });
  document.addEventListener("copy", onCopy);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("blur", onBlur);
  window.addEventListener("focus", onFocus);
  attached = true;
}

export function uninstallScreenshotDeterrent() {
  if (!attached) return;
  document.removeEventListener("contextmenu", onContextMenu);
  document.removeEventListener("dragstart", onDragStart);
  document.removeEventListener("keydown", onKeyDown, { capture: true });
  document.removeEventListener("copy", onCopy);
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("blur", onBlur);
  window.removeEventListener("focus", onFocus);
  if (watermarkInterval) { clearInterval(watermarkInterval); watermarkInterval = null; }
  document.getElementById(DETERRENT_STYLE_ID)?.remove();
  document.getElementById(BLANK_OVERLAY_ID)?.remove();
  document.querySelector(".flexpay-watermark")?.remove();
  setBlanked(false);
  attached = false;
}
